import { useEffect } from "react";
import { Lightbulb, Loader2, X } from "lucide-react";
import type { BuildGuideResult, BuildPath } from "../hooks/useInitiativeAssist";

type Props = {
  title: string;
  path?: BuildPath;
  loading: boolean;
  error: string | null;
  guide: BuildGuideResult | null;
  onRetry: () => void;
  onClose: () => void;
};

const PATH_LABELS: Record<BuildPath, string> = {
  citizen: "AI Builder",
  hybrid: "Hybrid",
  engineer: "Engineer needed",
};

/** Small AI-drafted "how to start building this" popup. Presentational only
 *  — the parent owns fetching and caching. */
export function BuildGuidePopup({
  title,
  path,
  loading,
  error,
  guide,
  onRetry,
  onClose,
}: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop guide-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal guide-modal" role="dialog" aria-modal="true" aria-label="Starting guidance">
        <div className="guide-head">
          <span className="guide-icon" aria-hidden>
            <Lightbulb size={18} />
          </span>
          <div className="guide-head-text">
            <span className="guide-kind">Starting guidance</span>
            <h3 className="guide-title">{title || "Untitled initiative"}</h3>
            {path ? <span className={`guide-path guide-path--${path}`}>{PATH_LABELS[path]}</span> : null}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="guide-body">
          {loading ? (
            <div className="guide-loading">
              <Loader2 size={16} className="add-item-spin" /> Drafting starting guidance…
            </div>
          ) : error ? (
            <div className="guide-error">
              <p>{error}</p>
              <button type="button" className="btn btn-ghost" onClick={onRetry}>
                Try again
              </button>
            </div>
          ) : guide ? (
            <>
              {guide.overview ? <p className="guide-overview">{guide.overview}</p> : null}
              <ol className="guide-steps">
                {guide.steps.map((s, i) => (
                  <li key={i}>
                    <span className="guide-step-num">{i + 1}</span>
                    <span>
                      <b>{s.title}</b>
                      {s.detail ? <em>{s.detail}</em> : null}
                    </span>
                  </li>
                ))}
              </ol>
              {guide.watchOuts.length > 0 ? (
                <div className="guide-watch">
                  <span className="guide-watch-label">Watch out for</span>
                  <ul>
                    {guide.watchOuts.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <p className="guide-note">
                AI-drafted starting point only — confirm the approach with IT and Security
                before building.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
